// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { AnalyticsConfig, AnalyticsProviderAdapter, ConsentState } from '../types';
import { createPlausibleAdapter } from '../providers/plausible';

export class AnalyticsManager {
  private adapters: AnalyticsProviderAdapter[] = [];
  private config: AnalyticsConfig | null = null;
  private userContext: { userId?: string; role?: string } = {};
  private consentState: ConsentState = { analytics: false };

  init(config: AnalyticsConfig): void {
    this.config = config;

    if (config.enabled === false) {
      this.debug('disabled via config');
      return;
    }

    if (config.plausible) {
      const adapter = createPlausibleAdapter(config.plausible, config.debug);
      this.adapters.push(adapter);
    }

    // Initialize adapters that don't require consent immediately
    for (const adapter of this.adapters) {
      if (!adapter.requiresConsent) {
        try {
          adapter.init();
          this.debug(`initialized adapter: ${adapter.name}`);
        } catch (e) {
          this.debug(`failed to init adapter ${adapter.name}:`, e);
        }
      }
    }
  }

  page(path: string, props?: Record<string, string>): void {
    const merged = this.mergeBaseProps(props);
    for (const adapter of this.getActiveAdapters()) {
      try {
        adapter.page(path, merged);
        this.debug(`page: ${path}`, merged);
      } catch (e) {
        this.debug(`page error (${adapter.name}):`, e);
      }
    }
  }

  track(event: string, props?: Record<string, string>): void {
    const merged = this.mergeBaseProps(props);
    for (const adapter of this.getActiveAdapters()) {
      try {
        adapter.track(event, merged);
        this.debug(`track: ${event}`, merged);
      } catch (e) {
        this.debug(`track error (${adapter.name}):`, e);
      }
    }
  }

  identify(userId: string, traits?: Record<string, string>): void {
    for (const adapter of this.getActiveAdapters()) {
      try {
        adapter.identify(userId, traits);
        this.debug(`identify: ${userId}`);
      } catch (e) {
        this.debug(`identify error (${adapter.name}):`, e);
      }
    }
  }

  setUser(userId: string, role: string): void {
    this.userContext = { userId, role };
    this.identify(userId, { role });
  }

  onConsentChange(state: ConsentState): void {
    this.consentState = state;

    for (const adapter of this.adapters) {
      if (!adapter.requiresConsent) continue;

      if (state.analytics) {
        try {
          adapter.init();
          this.debug(`consent-initialized adapter: ${adapter.name}`);
        } catch (e) {
          this.debug(`consent-init error (${adapter.name}):`, e);
        }
      } else {
        try {
          adapter.destroy();
          this.debug(`consent-destroyed adapter: ${adapter.name}`);
        } catch (e) {
          this.debug(`consent-destroy error (${adapter.name}):`, e);
        }
      }
    }
  }

  destroy(): void {
    for (const adapter of this.adapters) {
      try {
        adapter.destroy();
      } catch (e) {
        this.debug(`destroy error (${adapter.name}):`, e);
      }
    }
    this.adapters = [];
    this.config = null;
    this.userContext = {};
  }

  private getActiveAdapters(): AnalyticsProviderAdapter[] {
    return this.adapters.filter(
      (a) => !a.requiresConsent || this.consentState.analytics
    );
  }

  private mergeBaseProps(
    props?: Record<string, string>
  ): Record<string, string> {
    const base: Record<string, string> = {};
    if (this.config) {
      base.tenant_id = this.config.tenant;
      base.app = this.config.app;
    }
    if (this.userContext.role) {
      base.user_role = this.userContext.role;
    }
    if (this.userContext.userId) {
      base.user_id = this.userContext.userId;
    }
    return { ...base, ...props };
  }

  private debug(message: string, ...args: unknown[]): void {
    if (this.config?.debug) {
      console.debug(`[analytics] ${message}`, ...args);
    }
  }
}
